import { useEffect } from 'react';
import {
  SITE_URL,
  DEFAULT_OG_IMAGE,
  canonicalUrl,
  buildJsonLd,
  findPublicSeoPage,
} from '../../seo/publicPages';

/**
 * Sets document title + meta description/canonical/OG tags for public pages.
 * Also marks <html data-seo-ready="true"> so the postbuild prerender can wait.
 *
 * Optional: ogImage, jsonLd, article (publishedTime, modifiedTime, author, section).
 */
function useSeoMeta({
  title,
  description,
  path,
  ogType = 'website',
  ogImage,
  jsonLd: customJsonLd,
  article,
} = {}) {
  const articlePublished = article?.publishedTime || '';
  const articleModified = article?.modifiedTime || '';
  const articleAuthor = article?.author || '';
  const articleSection = article?.section || '';

  useEffect(() => {
    if (!title && !description && !path && !customJsonLd) return undefined;

    const page = path ? findPublicSeoPage(path) : null;
    const resolvedTitle = title || page?.title;
    const resolvedDescription = description || page?.description;
    const canonical = path ? canonicalUrl(path) : `${SITE_URL}${window.location.pathname}`;
    const image = ogImage || DEFAULT_OG_IMAGE;

    if (resolvedTitle) {
      document.title = resolvedTitle;
    }

    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([key, value]) => {
        if (value != null) el.setAttribute(key, value);
      });
      return el;
    };

    if (resolvedTitle) {
      ensureMeta('meta[name="title"]', { name: 'title', content: resolvedTitle });
      ensureMeta('meta[property="og:title"]', { property: 'og:title', content: resolvedTitle });
      ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: resolvedTitle });
    }

    if (resolvedDescription) {
      ensureMeta('meta[name="description"]', {
        name: 'description',
        content: resolvedDescription,
      });
      ensureMeta('meta[property="og:description"]', {
        property: 'og:description',
        content: resolvedDescription,
      });
      ensureMeta('meta[name="twitter:description"]', {
        name: 'twitter:description',
        content: resolvedDescription,
      });
    }

    ensureMeta('meta[name="robots"]', { name: 'robots', content: 'index, follow' });
    ensureMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    ensureMeta('meta[property="og:type"]', { property: 'og:type', content: ogType });
    ensureMeta('meta[property="og:image"]', { property: 'og:image', content: image });
    ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });

    if (ogType === 'article') {
      if (articlePublished) {
        ensureMeta('meta[property="article:published_time"]', {
          property: 'article:published_time',
          content: articlePublished,
        });
      }
      if (articleModified) {
        ensureMeta('meta[property="article:modified_time"]', {
          property: 'article:modified_time',
          content: articleModified,
        });
      }
      if (articleAuthor) {
        ensureMeta('meta[property="article:author"]', {
          property: 'article:author',
          content: articleAuthor,
        });
      }
      if (articleSection) {
        ensureMeta('meta[property="article:section"]', {
          property: 'article:section',
          content: articleSection,
        });
      }
    }

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);

    const schemaPayload = customJsonLd || (page ? buildJsonLd(page) : null);
    if (schemaPayload) {
      let jsonLdEl = document.getElementById('seo-json-ld');
      if (!jsonLdEl) {
        jsonLdEl = document.createElement('script');
        jsonLdEl.id = 'seo-json-ld';
        jsonLdEl.type = 'application/ld+json';
        document.head.appendChild(jsonLdEl);
      }
      jsonLdEl.textContent = JSON.stringify(schemaPayload);
    }

    document.documentElement.setAttribute('data-seo-ready', 'true');

    return () => {
      document.documentElement.removeAttribute('data-seo-ready');
    };
  }, [
    title,
    description,
    path,
    ogType,
    ogImage,
    customJsonLd,
    articlePublished,
    articleModified,
    articleAuthor,
    articleSection,
  ]);
}

export default useSeoMeta;
